import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import VideoChat from '@/components/VideoChat';
import TextChat from '@/components/TextChat';
import UserControls from '@/components/UserControls';
import { toast } from '@/utils/toast';
import { handleError } from '@/utils/error-handler';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDeviceSafetyCheck } from '@/hooks/use-device-safety-check';
import { 
  ChevronLeft,
  Star, 
  User, 
} from 'lucide-react';
import axios from 'axios';

const LiveChat: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isTextChatOpen, setIsTextChatOpen] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [isChatEnded, setIsChatEnded] = useState(false);
  const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(true);
  const [chatActive, setChatActive] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const roomIdRef = useRef<string | null>(null);
  const safetyCheckRef = useRef<number | null>(null);
  const controlsTimerRef = useRef<number | null>(null);
  const deviceSetupTimerRef = useRef<number | null>(null);
  
  const { 
    connectToRoom, 
    leaveRoom, 
    currentRoomId,
    connectionStatus,
    toggleMicrophone,
    isMicrophoneEnabled,
    isSearchingForPartner,
    onlineUsers,
    isVideoEnabled,
    toggleVideo,
    findNextPartner
  } = useChat();

  // Enhanced device safety checks
  const { forceDisableDevices } = useDeviceSafetyCheck(chatActive, isChatEnded);
  
  // Setup effect - enable devices and connect to room
  useEffect(() => {
    if (!currentUser) {
      toast.error("Please log in to access chat");
      navigate('/login');
      return;
    }
    
    // Generate room ID if needed
    if (!roomIdRef.current) {
      roomIdRef.current = `room-${Math.floor(Math.random() * 1000000)}`;
    }
    
    if (roomIdRef.current) {
      console.log("LiveChat: Enabling devices before connecting");
      
      // Immediately activate devices - critical for proper startup
      toggleVideo(true);
      toggleMicrophone(true);
      setChatActive(true);
      
      // Setup a cascading series of checks to ensure devices are enabled
      const setupTimers = [50, 150, 300, 500];
      
      setupTimers.forEach(delay => {
        deviceSetupTimerRef.current = window.setTimeout(() => {
          if (!isVideoEnabled) {
            console.log(`LiveChat: Camera not enabled after ${delay}ms, retrying...`);
            toggleVideo(true);
          }
          
          if (!isMicrophoneEnabled) {
            console.log(`LiveChat: Microphone not enabled after ${delay}ms, retrying...`);
            toggleMicrophone(true);
          }
        }, delay);
      });
      
      // Connect to room after devices are initialized
      const connectTimer = setTimeout(async () => {
        try {
          await connectToRoom(roomIdRef.current!);
          
          if (currentUser) {
            // Log chat session start
            try {
              await axios.post('http://localhost:3001/api/chat-sessions', {
                userId: currentUser.uid,
                sessionStart: new Date().toISOString(),
                roomId: roomIdRef.current
              });
            } catch (error) {
              console.error("Failed to log chat session start:", error);
            }
          }
        } catch (error) {
          handleError(error, {
            customMessage: "Failed to connect to chat. Please try again."
          });
        }
      }, 600);
      
      return () => {
        clearTimeout(connectTimer);
        
        if (deviceSetupTimerRef.current) {
          window.clearTimeout(deviceSetupTimerRef.current);
        }
      };
    }
    
    return () => {
      console.log("LiveChat component unmounting - cleaning up");
      
      // Complete shutdown sequence
      leaveRoom();
      forceDisableDevices();
      toggleMicrophone(false);
      toggleVideo(false);
      setChatActive(false);
      
      // Log chat session end
      if (currentUser && roomIdRef.current) {
        try {
          axios.put(`http://localhost:3001/api/chat-sessions/${roomIdRef.current}`, {
            userId: currentUser.uid,
            sessionEnd: new Date().toISOString()
          }).catch(error => {
            console.error("Failed to log chat session end:", error);
          });
        } catch (error) {
          console.error("Error logging chat session end:", error);
        }
      }
    };
  }, [currentUser, navigate, connectToRoom, leaveRoom, toggleMicrophone, toggleVideo, isVideoEnabled, isMicrophoneEnabled, forceDisableDevices]);
  
  const handleFindNext = useCallback(async () => {
    setShowRating(true);
  }, []);

  const handleEndChat = useCallback(() => {
    setIsChatEnded(true);
    setChatActive(false);
    leaveRoom();
    
    console.log("LiveChat: End chat - immediately disabling devices");
    
    // Immediate device shutdown
    forceDisableDevices();
    toggleMicrophone(false);
    toggleVideo(false);
    
    // Multiple safety checks to ensure devices are turned off
    [100, 300, 500].forEach(delay => {
      safetyCheckRef.current = window.setTimeout(() => {
        if (isMicrophoneEnabled) {
          console.warn(`LiveChat: Microphone still enabled ${delay}ms after chat ended. Turning off again...`);
          toggleMicrophone(false);
        }
        
        if (isVideoEnabled) {
          console.warn(`LiveChat: Camera still enabled ${delay}ms after chat ended. Turning off again...`);
          toggleVideo(false);
        }
      }, delay);
    });
    
    // Log chat session end
    if (currentUser && roomIdRef.current) {
      axios.put(`http://localhost:3001/api/chat-sessions/${roomIdRef.current}`, {
        userId: currentUser.uid,
        sessionEnd: new Date().toISOString()
      }).catch(error => {
        console.error("Failed to log chat session end:", error);
      });
    }
    
    // Navigate away after a short delay
    const timer = setTimeout(() => {
      navigate('/');
    }, 2000);
    
    return () => {
      clearTimeout(timer);
      if (safetyCheckRef.current) {
        clearTimeout(safetyCheckRef.current);
      }
    };
  }, [leaveRoom, navigate, toggleMicrophone, toggleVideo, isMicrophoneEnabled, isVideoEnabled, currentUser, forceDisableDevices]);

  const handleNextPartner = useCallback(async () => {
    setShowRating(false);
    
    try {
      if (currentRoomId) {
        leaveRoom();
      }
      
      roomIdRef.current = `room-${Math.floor(Math.random() * 1000000)}`;
      
      console.log("LiveChat: Enabling devices for new connection");
      
      // Immediately activate devices for the new connection
      toggleVideo(true);
      toggleMicrophone(true);
      setChatActive(true);
      
      // Setup a cascading series of checks to ensure devices are enabled
      const setupTimers = [50, 150, 300];
      
      setupTimers.forEach(delay => {
        deviceSetupTimerRef.current = window.setTimeout(() => {
          if (!isVideoEnabled) {
            console.log(`LiveChat: Camera not enabled for new partner after ${delay}ms, retrying...`);
            toggleVideo(true);
          }
          
          if (!isMicrophoneEnabled) {
            console.log(`LiveChat: Microphone not enabled for new partner after ${delay}ms, retrying...`);
            toggleMicrophone(true);
          }
        }, delay);
      });
      
      // Connect to a new partner after devices are initialized
      setTimeout(async () => {
        try {
          await findNextPartner();
          toast.success("Finding a new partner...");
          
          // Log new chat session
          if (currentUser && roomIdRef.current) {
            try {
              await axios.post('http://localhost:3001/api/chat-sessions', {
                userId: currentUser.uid,
                sessionStart: new Date().toISOString(),
                roomId: roomIdRef.current
              });
            } catch (error) {
              console.error("Failed to log new chat session:", error);
            }
          }
          
          if (soundEffectsEnabled) {
            try {
              const audio = new Audio('/sounds/switch.mp3');
              audio.volume = 0.3;
              audio.play().catch(e => console.log('Audio play prevented:', e));
            } catch (e) {
              console.error('Audio play error:', e);
            }
          }
        } catch (error) {
          handleError(error, {
            customMessage: "Failed to find a new partner. Please try again."
          });
        }
      }, 400);
    } catch (error) {
      handleError(error, {
        customMessage: "Failed to find a new partner. Please try again."
      });
    }
  }, [findNextPartner, soundEffectsEnabled, currentUser, leaveRoom, toggleMicrophone, toggleVideo, currentRoomId, isVideoEnabled, isMicrophoneEnabled]);

  const handleSubmitRating = useCallback((rating: number) => {
    toast.success(`Rated user ${rating} stars!`);
    handleNextPartner();
  }, [handleNextPartner]);

  const toggleTextChat = useCallback(() => {
    setIsTextChatOpen(prev => !prev);
    
    if (soundEffectsEnabled) {
      const audio = new Audio('/sounds/click.mp3');
      audio.volume = 0.1;
      audio.play().catch(e => console.log('Audio play prevented:', e));
    }
  }, [soundEffectsEnabled]);
  
  // Exit handler with proper device cleanup
  const handleExit = useCallback(() => {
    console.log("LiveChat: Exit button clicked - shutting down devices");
    
    // Immediate device shutdown sequence
    toggleMicrophone(false);
    toggleVideo(false);
    setChatActive(false);
    forceDisableDevices();
    
    // Double-check device shutdown after a short delay
    setTimeout(() => {
      if (isMicrophoneEnabled || isVideoEnabled) {
        console.warn("LiveChat: Devices still on during exit. Forcing off again...");
        toggleMicrophone(false);
        toggleVideo(false);
      }
    }, 100);
    
    navigate('/');
  }, [navigate, toggleMicrophone, toggleVideo, isMicrophoneEnabled, isVideoEnabled, forceDisableDevices]);
  
  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#0a0e1e] relative overflow-hidden">
      <Helmet>
        <title>Live Chat | Chatrix</title>
        <meta name="description" content="Connect with people around the world through video and text chat" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>
      
      <AnimatePresence>
        {controlsVisible && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="absolute top-0 left-0 right-0 p-3 flex justify-between items-center z-50 bg-[#0a0e1e]/90 backdrop-blur-md"
          >
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExit}
              className="p-2 rounded-full flex items-center space-x-2 hover:bg-white/10 transition-all text-white"
            >
              <ChevronLeft size={16} />
              <span className="text-sm font-medium">Exit</span>
            </motion.button>
            
            <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center">
              <User size={16} className="text-white" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="flex flex-1 pt-12">
        <div className={`flex-1 transition-all duration-300 ${isTextChatOpen ? (isMobile ? "w-0" : "w-2/3") : "w-full"}`}>
          {isChatEnded ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-white">
                <h2 className="text-2xl font-bold mb-4 font-sans">Chat Ended</h2>
                <p className="mb-6 font-sans">Returning to homepage...</p>
              </div>
            </div>
          ) : (
            <VideoChat isActive={true} isConnected={connectionStatus === 'connected'} />
          )}
        </div>
        
        <AnimatePresence mode="wait">
          {isTextChatOpen && (isSearchingForPartner || connectionStatus === 'connected') && !isChatEnded && (
            <TextChat 
              isOpen={true} 
              key="text-chat"
              soundEffects={soundEffectsEnabled}
            />
          )}
        </AnimatePresence>
      </div>
      
      {!isChatEnded && (
        <UserControls
          onToggleTextChat={toggleTextChat}
          onNext={handleFindNext}
          onEnd={handleEndChat}
          isTextChatActive={isTextChatOpen}
          position="bottom-center"
          isVisible={controlsVisible}
        />
      )}
      
      <AnimatePresence>
        {showRating && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-[#0f172a] p-8 rounded-2xl max-w-md w-full border border-white/10 shadow-xl"
            >
              <h3 className="text-xl font-medium mb-8 text-center text-white">Rate your experience</h3>
              
              <div className="flex justify-center space-x-6 mb-10">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <motion.button
                    key={rating}
                    whileHover={{ scale: 1.1, y: -5 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleSubmitRating(rating)}
                    className="p-2 transition-all focus:outline-none group"
                  >
                    <Star 
                      size={32} 
                      className="transition-all duration-200 text-white/40 group-hover:text-yellow-300 group-hover:fill-yellow-300"
                      fill="transparent"
                    />
                  </motion.button>
                ))}
              </div>
              
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowRating(false)}
                  className="px-6 py-2 bg-transparent border border-white/20 text-white rounded-md hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNextPartner}
                  className="px-6 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors"
                >
                  Next Partner
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveChat;
